import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Film, MessageCircle, Sparkles, User, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useSectionsVisibility } from '../hooks/useSectionsVisibility';
import axios from 'axios';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const { homeHidden } = useSectionsVisibility();
  const [staffUnread, setStaffUnread] = useState(0);
  const [messagesUnread, setMessagesUnread] = useState(0);

  useEffect(() => {
    if (user?.is_admin) {
      loadStaffUnread();
      const interval = setInterval(loadStaffUnread, 15000);
      return () => clearInterval(interval);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    loadMessagesUnread();
    const interval = setInterval(loadMessagesUnread, 15000);
    return () => clearInterval(interval);
  }, [user]);

  const loadStaffUnread = async () => {
    try {
      const res = await axios.get('/api/staff-chat/unread-count');
      setStaffUnread(res.data?.unread_count || 0);
    } catch (e) {}
  };

  const loadMessagesUnread = async () => {
    try {
      const res = await axios.get('/api/messages/unread-count');
      setMessagesUnread(res.data?.count || 0);
    } catch (e) {}
  };

  const navItems = [
    ...(homeHidden ? [] : [{ path: '/feed', icon: Home, label: t('nav.home') }]),
    { path: '/films', icon: Film, label: t('nav.discover') },
    { path: '/messages', icon: MessageCircle, label: t('nav.messages'), badge: messagesUnread },
    { path: '/film-ia', icon: Sparkles, label: 'IA' },
    { path: '/profile', icon: User, label: t('nav.profile') }
  ];

  // Ajouter Admin pour les administrateurs
  if (user?.is_admin) {
    navItems.splice(navItems.length, 0, { path: '/admin', icon: LayoutDashboard, label: t('nav.admin'), badge: staffUnread });
  }

  return (
    <div className={`fixed bottom-0 left-0 right-0 backdrop-blur-md border-t border-white/10 z-50 ${isDark ? 'bg-black/90' : ''}`} style={!isDark ? { background: 'var(--theme-nav-bg)' } : undefined}>
      <div className="flex justify-around overflow-x-auto scrollbar-hide items-center p-2 max-w-screen-xl mx-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all min-w-[60px] flex-shrink-0 relative ${
                isActive
                  ? 'text-magenta-500'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <div className="relative">
                <Icon className="w-6 h-6" />
                {item.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Navigation;
